/**
 * 設備服務工具
 * 統一管理平台判斷邏輯，嚴禁在業務代碼中直接使用原生 API
 */

// 擴展 Window 接口以包含 Capacitor
declare global {
  interface Window {
    Capacitor?: {
      getPlatform(): string
    }
  }
}

// 定義設備信息類型
interface DeviceInfo {
  uniqueId: string;
  deviceType: string;
  deviceName: string;
  osType: 'web' | 'ios' | 'android';
  channel: string;
  bundleId: string;
  language: string;
  version: string;
}

export class DeviceService {
  private static instance: DeviceService
  private platform: 'web' | 'ios' | 'android' = 'web'

  private constructor() {
    this.detectPlatform()
  }

  public static getInstance(): DeviceService {
    if (!DeviceService.instance) {
      DeviceService.instance = new DeviceService()
    }
    return DeviceService.instance
  }

  private detectPlatform(): void {
    // 使用 Capacitor 進行平台判斷
    if (typeof window !== 'undefined' && window.Capacitor) {
      const platform = window.Capacitor.getPlatform()
      if (platform === 'ios') {
        this.platform = 'ios'
      } else if (platform === 'android') {
        this.platform = 'android'
      } else {
        this.platform = 'web'
      }
    } else {
      this.platform = 'web'
    }
  }

  /**
   * 是否為原生 APP（iOS/Android）
   */
  public isNative(): boolean {
    return this.platform === 'ios' || this.platform === 'android'
  }

  /**
   * 是否為 Web 環境
   */
  public isWeb(): boolean {
    return this.platform === 'web'
  }

  /**
   * 獲取平台類型
   */
  public getPlatform(): 'web' | 'ios' | 'android' {
    return this.platform
  }

  /**
   * 是否為 iOS 平台
   */
  public isIOS(): boolean {
    return this.platform === 'ios'
  }

  /**
   * 是否為 Android 平台
   */
  public isAndroid(): boolean {
    return this.platform === 'android'
  }

  /**
   * 獲取設備信息
   */
  public getDeviceInfo(): DeviceInfo {
    if (this.isNative()) {
      // 在原生環境中獲取設備信息
      return {
        uniqueId: this.generateUniqueId(),
        deviceType: this.getDeviceType(),
        deviceName: this.getDeviceName(),
        osType: this.platform,
        channel: 'UUspa',
        bundleId: 'com.uuspa.app',
        language: this.getLanguage(),
        version: '1.0.0'
      }
    } else {
      // Web 環境的設備信息
      return {
        uniqueId: this.generateUniqueId(),
        deviceType: 'web',
        deviceName: 'web_browser',
        osType: 'web',
        channel: 'UUspa',
        bundleId: 'com.uuspa.web',
        language: this.getLanguage(),
        version: '1.0.0'
      }
    }
  }

  private generateUniqueId(): string {
    // 生成唯一設備 ID
    return 'web_' + Math.random().toString(36).substr(2, 9)
  }

  private getDeviceType(): string {
    // 獲取設備類型
    if (this.isWeb()) {
      return 'web'
    }
    // 在原生環境中可以獲取更詳細的設備信息
    return this.platform
  }

  private getDeviceName(): string {
    // 獲取設備名稱
    if (this.isWeb()) {
      return 'web_browser'
    }
    // 在原生環境中可以獲取實際設備名稱
    return `${this.platform}_device`
  }

  private getLanguage(): string {
    // 獲取語言設置
    const language = navigator.language || 'zh-TW'
    return language.toLowerCase()
  }
}

// 導出單例實例
export const deviceService = DeviceService.getInstance()
